// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Trans } from "@lingui/react"
import styled, { css } from "styled-components/macro"
import { darken } from "polished"
import { t } from "@lingui/macro"
import { autoUpdate, flip, limitShift, shift, useFloating } from "@floating-ui/react-dom"
import RemoveButton from "../../shared/RemoveButton"
import {
  ActiveListItem, LinkUnderlineSharedStyles,
  ListItemDescription,
  ListItemLabel,
  NoDescriptionText,
  SharedDropdownContent,
  TagOverlayButton,
  TagOverlaySvgIcon,
  TextEllipsis,
} from "../../shared/styles"
import * as TagsActions from "../../tags/actions"
import * as TagsSlice from "../../tags/slice"
import * as TimelineActions from "../actions"
import {
  AttachTagRequest,
  DetachTagCategoryRequest,
  DetachTagRequest,
  IdList,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { TagRef } from "../../../generated/wisetime/user/type/tag_ref_pb"
import { Tag } from "../../../generated/legebill/type/tag_pb"
import { DestinationOneOf } from "../../shared/search-dropdown/types"
import { selectTagColor } from "../slices/timeline"
import { RootState } from "../../../app/store"
import { getTagCategoryDescription, IdsList } from "../../tags/tags"
import { TagsViewType } from "../slices/additionalUi"
import { TagCategory } from "../../../generated/legebill/type/tag_category_pb"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"

export const TAG_CATEGORIES_DROPDOWN_CLASSNAME = "tl-tag-categories-dropdown"
export const TAG_CATEGORIES_DROPDOWN_HEADER_CLASSNAME = "tl-tag-categories-dropdown-header"

export const DropdownContent = styled.div<{ noItems: boolean, isTagDropdown: boolean }>`
  ${SharedDropdownContent};
  ${({ isTagDropdown }) => !isTagDropdown && css`
    max-width: 100%;
  `};
  width: ${({ noItems }) => noItems ? "224px" : "450px"};
  z-index: 105;
  display: none;
  flex-direction: column;
  align-items: flex-start;
  max-height: ${({ theme }): string => theme.searchDropdownListHeight}px;
  overflow-y: auto;
`

const ArrowIcon = styled.span`
  display: inline-block;
  border-top: 4px solid ${({ theme }): string => theme.colors.white};
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  margin-left: 7px;
`

export const DropdownHeader = styled.div<{ tagCategoryColor: string }>`
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 2px;
  color: ${({ theme }) => theme.colors.white};
  height: 22px;
  line-height: 22px;
  background-color: ${({ tagCategoryColor }) => darken(0.4, tagCategoryColor)};
  padding: 0 5px;

  ${RemoveButton} {
    z-index: 101;
  }

  &:hover {
    ${RemoveButton} {
      display: inline-block;
    }
  }
`

export const DropdownHeaderTitle = styled.span`
  ${TextEllipsis};
  font-size: 1.1rem;
  font-weight: 600;
  max-width: 125px;
`

const Dropdown = styled.div<{ tagCategoryColor: string }>`
  position: relative;
  
  &:hover {
    ${DropdownHeader} {
      background-color: ${({ tagCategoryColor }) => darken(0.5, tagCategoryColor)};
    }
    
    ${DropdownContent} {
      display: flex;
    }
    
    ${ArrowIcon} {
      transform: rotate(180deg);
    }
  }
`

const ListItem = styled.button<{
  isMessage?: boolean,
  isAdded?: boolean,
}>`
  width: 100%;
  padding: .5rem 1.2rem;
  text-align: left;
  
  ${({ isMessage }) => {
    if (isMessage) {
      return css`
        font-weight: 400;
        cursor: default;
        line-height: normal;
        border-radius: 2px;
        color: ${({ theme }) => theme.colors.charcoal};
        background-color: ${({ theme }) => theme.colors.white};
      `
    }
    return css`
      &:nth-child(2n) {
        background-color: ${({ theme })=> theme.colors.alabaster};
      }

      &:first-child {
        border-radius: 3px 3px 0 0;
      }

      &:last-child {
        border-radius: 0 0 3px 3px;
      }
    `
  }};

  ${({ isAdded, theme }) => {
    if (isAdded) {
      return css`
        cursor: default;
        
        ${ListItemLabel} {
          color: ${theme.colors.silverChalice};
        }
        
        ${ListItemDescription} {
          color: ${theme.colors.silverChalice};
        }
      `
    }
    return css`
      &:hover {
        ${ActiveListItem};
      }
    `
  }}
`

const RemoveHelper = styled.button`
  ${LinkUnderlineSharedStyles};
  font-weight: 600;
`

const StyledTagOverlayButton = styled(TagOverlayButton)`
  &:after {
    content: "";
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    bottom: -1px;
  }
`

export const renderLabel = (tagsViewType: TagsViewType, tagCategory: TagCategory.AsObject) => {
  if (tagsViewType === TimelineAdditionalUiSlice.TagsViewType.DESCRIPTION) {
    return tagCategory.description || (
      <NoDescriptionText>
        {tagCategory.label + " - " + t({ id: "label_no_description", message: "No description." }).replace(".", "")}
      </NoDescriptionText>
    )
  }
  return tagCategory.label
}

type Props = {
  tagCategoryId: string,
  timeGroupIdOrTimeRowsIds: DestinationOneOf,
  addedTagIds?: IdsList,
  inTimesheetGrid?: boolean,
  tagCategoryLabel?: string,
  tagColor?: string,
  className?: string,
  detachTagRequest?: DetachTagRequest,
}

const TagCategoriesDropdown = ({
  tagCategoryId,
  timeGroupIdOrTimeRowsIds,
  addedTagIds = [],
  inTimesheetGrid = false,
  tagCategoryLabel,
  tagColor,
  className,
  detachTagRequest,
}: Props) => {
  const dispatch = useDispatch()
  const makeSelectTagCategoryById = useMemo(TagsSlice.selectTagCategoryById, [])
  const tagCategory = useSelector((state: RootState) => makeSelectTagCategoryById(state, tagCategoryId))
  const makeSelectTagsByCategoryId = useMemo(TagsSlice.selectTagsByCategoryId, [])
  const tags = useSelector((state: RootState) => makeSelectTagsByCategoryId(state, tagCategoryId))
  const selectColor = useMemo(selectTagColor, [])
  const tagCategoryColor = useSelector((state: RootState) => selectColor(state, tagCategoryId))
  const tagsViewType = useSelector(TimelineAdditionalUiSlice.selectTagsViewType)

  const [isInReplaceProgress, setIsInReplaceProgress] = useState(false)
  const classNames = `${TAG_CATEGORIES_DROPDOWN_CLASSNAME} ${className ?? ""}`
  const isTimeRowIds = Array.isArray(timeGroupIdOrTimeRowsIds)

  const handleClick = ({ siteId, id, name }: Tag.AsObject) => {
    if (isInReplaceProgress) {
      return
    }
    const attachTagRequest = new AttachTagRequest().setTagId(id)
    if (isTimeRowIds) {
      attachTagRequest.setTimeRowIds(new IdList().setIdsList(timeGroupIdOrTimeRowsIds))
    } else {
      attachTagRequest.setTimeGroupIds(new IdList().setIdsList([timeGroupIdOrTimeRowsIds]))
    }
    const tag = new TagRef().setId(id).setSiteId(siteId).setName(name)
    if (detachTagRequest) {
      setIsInReplaceProgress(true)
      dispatch(TimelineActions.replaceTag({
        attachTagRequest,
        detachTagRequest,
        tag,
        onError: () => setIsInReplaceProgress(false),
      }))
    } else {
      dispatch(TimelineActions.attachTag({ attachTagRequest, tag }))
    }
  }

  const handleMouseEnter = () => {
    dispatch(TagsActions.listTagsByCategory({
      tagCategoryId,
      swallowFailure: true,
    }))
  }

  const {
    refs,
    floatingStyles,
  } = useFloating({
    strategy: "fixed",
    placement: inTimesheetGrid ? "bottom-start" : "bottom-end",
    middleware: [
      flip(),
      shift({ limiter: limitShift() }),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true,
      }),
  })

  const handleRemove = (): void => {
    const request = new DetachTagCategoryRequest().setTagCategoryId(tagCategoryId)
    if (isTimeRowIds) {
      request.setTimeRowIds(new IdList().setIdsList(timeGroupIdOrTimeRowsIds))
    } else {
      request.setTimeGroupId(timeGroupIdOrTimeRowsIds)
    }
    if (request.getTargetCase() !== DetachTagCategoryRequest.TargetCase.TARGET_NOT_SET) {
      dispatch(TimelineActions.detachTagCategory(request))
    }
  }

  const renderDropdownContent = (): React.ReactNode => {
    if (tags === undefined) {
      return null
    }

    if (tags.length === 0) {
      return (
        <ListItem
          isMessage
          as="span"
        >
          <Trans
            id="label_no_tags_in_category"
            message="No tags yet in this category. <0>Remove</0>"
            components={[
              <RemoveHelper type="button" key="0" onClick={handleRemove}/>,
            ]}
          />
        </ListItem>
      )
    }

    return (
      tags.map(tag => (
        <ListItem
          key={tag.id}
          type="button"
          isAdded={addedTagIds.includes(tag.id)}
          onClick={() => handleClick(tag)}
        >
          <ListItemLabel>{tag.name}</ListItemLabel>
          <ListItemDescription title={tag.description}>{tag.description}</ListItemDescription>
        </ListItem>
      ))
    )
  }

  const renderHeader = () => {
    const label = tagCategory ? tagCategory.label + "\n" + getTagCategoryDescription(tagCategory)
      : tagCategoryLabel ?? "..."
    if (tagColor) {
      return (
        <StyledTagOverlayButton
          as="div"
          ref={refs.setReference}
          className={TAG_CATEGORIES_DROPDOWN_HEADER_CLASSNAME}
          tabIndex={-1}
          title={label}
          tagColor={tagColor}
        >
          <TagOverlaySvgIcon name="tags"/>
        </StyledTagOverlayButton>
      )
    }

    return (
      <DropdownHeader
        ref={refs.setReference}
        className={TAG_CATEGORIES_DROPDOWN_HEADER_CLASSNAME}
        tagCategoryColor={tagCategoryColor}
      >
        <DropdownHeaderTitle
          title={label}
        >
          {tagCategory ? renderLabel(tagsViewType, tagCategory) : label}
        </DropdownHeaderTitle>
        <ArrowIcon/>
        <RemoveButton onClick={handleRemove}/>
      </DropdownHeader>
    )
  }

  if (tagColor && (
    tags === undefined
    || tags.length === 0
    || tags.every(t => addedTagIds.includes(t.id))
  )) {
    return null
  }

  return (
    <Dropdown
      className={classNames}
      tagCategoryColor={tagCategoryColor}
      onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      onMouseEnter={handleMouseEnter}
    >
      {renderHeader()}
      <DropdownContent
        ref={refs.setFloating}
        style={floatingStyles}
        noItems={tags?.length === 0}
        isTagDropdown={tagColor !== undefined}
      >
        {renderDropdownContent()}
      </DropdownContent>
    </Dropdown>
  )
}

export default TagCategoriesDropdown
